import * as React from 'react';
import { useMemo, useState } from 'react';
import { Stack, Box } from '@mui/material';
import User, { Company, CompanyEmployee } from '../../../interfaces/users.interface';
import StandardDialog from '../../Modals/StandardDialog';
import { DataCell, DataRow } from '../../Tables/DataList';
import { tightTableSx } from '../../Tables/dataTableStyles';
import { UserDataRow } from '../../Users/Users';
import canAdminCompany from '../../../services/adminUtils';
import { useAppSelector } from '../../../app/hooks';
import NewUserForm from '../../Users/NewUserForm';
import { USER_TYPES, addCompanyEmployeeMembership } from '../../../services/user';
import { COMPANY_TYPES } from '../../../services/vendor';
import { getCompanyUsers } from '../../../services/companyDetail';
import { useCompanyResource } from '../../Vendors/useCompanyResource';
import { TabEmpty, TabSkeletonRows, DEFAULT_PER_PAGE } from '../../Vendors/companyTabUi';
import { useSectionPager } from '../../Vendors/sectionPager';

type UsersTabProps = {
  company: Company;
  companyCallback: (company: Company) => void;
  createUserOpen?: boolean;
  onCreateUserClose?: () => void;
  userCanAdminCompany?: boolean;
  userCount?: number;
  onMembershipChange?: () => void;
};

export default function UsersTab(props: UsersTabProps) {
  const { user: adminUser } = useAppSelector(state => state.user);
  const { company, createUserOpen: externalCreateUserOpen, onCreateUserClose, onMembershipChange } = props;
  const [internalCreateUserOpen, setInternalCreateUserOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PER_PAGE);

  const { items: users, count, loading, refetch } = useCompanyResource<CompanyEmployee>(
    getCompanyUsers,
    company.id,
    page,
    pageSize,
  );
  useSectionPager({ count, page, pageSize, setPage, setPageSize });

  const createUserOpen = externalCreateUserOpen !== undefined ? externalCreateUserOpen : internalCreateUserOpen;

  const handleCreateUserClose = () => {
    if (onCreateUserClose) {
      onCreateUserClose();
    } else {
      setInternalCreateUserOpen(false);
    }
  };

  const handleMembershipChange = () => {
    refetch();
    onMembershipChange?.();
  };

  const addUserSuccessCallback = (user: User) => {
    if (!company.id || !user.id) return;
    // Targeted endpoint adds the user (and, for a TPN admin, promotes them and
    // sets the primary contact) server-side — no whole-company round-trip.
    addCompanyEmployeeMembership(company.id, user.id).then(() => {
      handleMembershipChange();
      handleCreateUserClose();
    });
  };

  const getUserTypeFromCompany = () => {
    if (company.type == COMPANY_TYPES.ASSESSOR) return USER_TYPES.ASSESSOR;
    if (company.type == COMPANY_TYPES.CONTENT_OWNER) return USER_TYPES.CONTENT_OWNER;
    if (company.type == COMPANY_TYPES.VENDOR) return USER_TYPES.VENDOR_ADMIN;
    return USER_TYPES.TPN_ADMIN;
  };

  const userCanAdminCompany: boolean = props.userCanAdminCompany ?? canAdminCompany(adminUser, company);
  const isTpnAdmin = useMemo(() => adminUser?.type === USER_TYPES.TPN_ADMIN, [adminUser]);
  const userCount = props.userCount ?? users.length;

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <StandardDialog title="Invite User" handleClose={handleCreateUserClose} isOpen={createUserOpen}>
        <NewUserForm successCallback={addUserSuccessCallback} userType={getUserTypeFromCompany()} modalClose={() => handleCreateUserClose()} />
      </StandardDialog>

      <Box sx={tightTableSx}>
        <DataRow onClick={() => {}} header>
          <DataCell xs={3}>Email</DataCell>
          <DataCell xs={2}>First</DataCell>
          <DataCell xs={2}>Last</DataCell>
          <DataCell xs={2}>Last Login</DataCell>
          <DataCell xs={1}>Admin</DataCell>
          <DataCell xs={1}>Consultant</DataCell>
          <DataCell xs={1}>&nbsp;</DataCell>
        </DataRow>

        {loading && <TabSkeletonRows rows={5} columns={[3, 2, 2, 2, 1, 1, 1]} />}

        {!loading && users.length === 0 && <TabEmpty label='No users' />}

        {!loading && users.map((user: CompanyEmployee, index: number) => (
          <UserDataRow
            key={user.id}
            company={company}
            user={user}
            index={index}
            handleUserDelete={handleMembershipChange}
            updateCompanyCallback={props.companyCallback}
            editUsersCallback={() => refetch()}
            includeAdminActionContent={true}
            isTpnAdmin={isTpnAdmin}
            userCanAdminCompany={userCanAdminCompany}
            isOnlyEmployee={userCount === 1}
          />
        ))}
      </Box>

    </Stack>
  );
}
